// Task #312 — shared focused-measure agent panel used by both the
// Teams view's "By team" and "By assessment" surfaces. When a
// user clicks a measure bar inside either surface, we render this
// panel (agents-first) so the drilldown is consistent across both
// modes:
//
//   AGENTS · [FOCUSED MEASURE]
//   Focused: [Measure name]  [All] [Present only]  [Clear filter]
//   <agent rows: name | score% | Δpp | call count | →>
//
// Clicking an agent row drills into that agent's calls via
// ``onAgentDrillNav`` with the focused measure pre-selected; calls
// only appear after this further drill, never directly from the
// measure-bar click. The panel respects the global Team + Agent
// filters via the ``agentScopeId`` prop — when set, only that agent
// is shown (or the empty state when they have no data for the
// focused measure).
import { useMemo, useState } from "react";
import { AlertTriangle, ArrowRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import { api, type AgentMeasureRow, type RangeMode, type TimeRange } from "@/lib/api";
import { agentSlug } from "@/lib/agents.helpers";

import { formatDeltaPp } from "@/lib/triage";
import { buildUrl } from "@/lib/router";
import type { AgentDrillNav } from "@/views/TeamsDashboardView";
import type { MeasureFocus } from "./MeasureBarChart";
import { Tooltip } from "./Tooltip";

export type OutcomeFilter = "all" | "present";

export function FocusedMeasureAgentPanel({
  teamId,
  focused,
  rangeFrom,
  rangeTo,
  priorFrom,
  priorTo,
  timeRange,
  rangeMode,
  assessmentId,
  // Global agent filter from the Teams view page header. When
  // set, the focused list is filtered down to just that agent (or
  // empty if they have no data for the measure in this scope).
  agentScopeId,
  onClear,
}: {
  teamId: string;
  focused: MeasureFocus;
  rangeFrom: string | null;
  rangeTo: string | null;
  priorFrom: string | null;
  priorTo: string | null;
  timeRange: TimeRange;
  rangeMode: RangeMode;
  assessmentId: string | null;
  agentScopeId: string | null;
  onAgentDrillNav: AgentDrillNav;
  onClear: () => void;
}) {
  const [outcomeFilter, setOutcomeFilter] = useState<OutcomeFilter>("present");

  // Per-agent detection rate for the focused (measure, label_name)
  // pair. Reuses the existing ``teamAgentsByMeasure`` endpoint — no
  // new backend route.
  const currQ = useQuery<AgentMeasureRow[]>({
    queryKey: [
      "team-agents-by-measure",
      teamId,
      rangeFrom,
      rangeTo,
      assessmentId,
      focused.measure_id,
      focused.label_name,
    ],
    queryFn: () =>
      api.teamAgentsByMeasure(teamId, {
        from: rangeFrom!,
        to: rangeTo!,
        assessment_id: assessmentId,
        measure_id: focused.measure_id,
        label_name: focused.label_name,
      }),
    enabled: !!rangeFrom && !!rangeTo,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });

  // Prior-period query — same endpoint, prior window. Drives the
  // per-row Δpp delta chip. Skipped when the active range has no
  // defined prior (e.g. All time) so the chip falls back to an
  // em-dash.
  const priorQ = useQuery<AgentMeasureRow[]>({
    queryKey: [
      "team-agents-by-measure-prior",
      teamId,
      priorFrom,
      priorTo,
      assessmentId,
      focused.measure_id,
      focused.label_name,
    ],
    queryFn: () =>
      api.teamAgentsByMeasure(teamId, {
        from: priorFrom!,
        to: priorTo!,
        assessment_id: assessmentId,
        measure_id: focused.measure_id,
        label_name: focused.label_name,
      }),
    enabled: !!priorFrom && !!priorTo,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });

  const priorMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of priorQ.data ?? []) m.set(r.agent_id, r.detection_rate);
    return m;
  }, [priorQ.data]);

  // Scope to a single agent when the Teams view has one
  // selected globally. The triage list already enforces this
  // client-side; we mirror it here so both modes behave the same.
  const scoped = useMemo(() => {
    const all = currQ.data ?? [];
    if (!agentScopeId) return all;
    return all.filter((a) => a.agent_id === agentScopeId);
  }, [currQ.data, agentScopeId]);

  const filtered = useMemo(() => {
    if (outcomeFilter === "all") return scoped;
    return scoped.filter((a) => a.detection_rate > 0);
  }, [scoped, outcomeFilter]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const primary = b.detection_rate - a.detection_rate;
      if (primary !== 0) return primary;
      return (a.agent_name ?? "").localeCompare(b.agent_name ?? "");
    });
  }, [filtered]);

  const outcomeOptions: { value: OutcomeFilter; label: string }[] = [
    { value: "all", label: "All" },
    { value: "present", label: "Present only" },
  ];

  return (
    <div
      className="space-y-2"
      data-testid="focused-measure-agent-panel"
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          onClear();
        }
      }}
    >
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Agents · {focused.label_name.toUpperCase()}
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-muted-foreground">
          Focused:{" "}
          <span className="text-foreground font-medium">
            {focused.label_name}
          </span>
        </span>
        <div
          role="group"
          aria-label="Outcome filter"
          className="inline-flex rounded-md border border-input bg-card overflow-hidden"
        >
          {outcomeOptions.map((opt) => {
            const active = opt.value === outcomeFilter;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setOutcomeFilter(opt.value)}
                aria-pressed={active}
                className={`px-2.5 py-1 border-r border-border last:border-r-0 ${
                  active
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground hover:bg-muted/40"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={() => {
            setOutcomeFilter("present");
            onClear();
          }}
          className="rounded border border-input bg-card px-2 py-1 text-foreground hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Clear filter
        </button>
      </div>
      {currQ.isLoading ? (
        <div
          className="bg-muted/40 rounded h-12 animate-pulse"
          aria-hidden="true"
        />
      ) : currQ.error ? (
        <div className="text-xs text-destructive flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            {(currQ.error as { message?: string })?.message ||
              "Failed to load agents."}
          </span>
          <button
            type="button"
            onClick={() => currQ.refetch()}
            className="ml-auto inline-flex items-center gap-1 rounded border border-input bg-card px-2 py-0.5 text-xs hover:bg-muted/40"
          >
            Retry
          </button>
        </div>
      ) : sorted.length === 0 ? (
        <div
          className="text-xs text-muted-foreground italic"
          data-testid="focused-measure-agent-panel-empty"
        >
          No agents found for this measure in the selected scope.
        </div>
      ) : (
        <div className="space-y-1">
          {sorted.map((a) => {
            const slug = agentSlug({
              full_name: a.agent_name,
              email: null,
              agent_id: a.agent_id,
            });
            const label = a.agent_name ?? "Unknown agent";
            const pct = Math.round(a.detection_rate * 100);
            const prior = priorMap.get(a.agent_id);
            const delta = prior !== undefined ? a.detection_rate - prior : null;
            const deltaText = delta !== null ? formatDeltaPp(delta) : "";
            const deltaTone =
              delta === null || delta === 0
                ? "bg-muted/40 text-muted-foreground"
                : delta > 0
                  ? "bg-primary/15 text-primary"
                  : "bg-destructive/15 text-destructive";
            const callLabel = a.call_count === 1 ? "call" : "calls";
            // Task #370 follow-up — open the agent profile in a new
            // tab so clicking through from the focused-measure agent
            // list keeps the user's Teams view context intact.
            // We render an anchor (not a button + onClick) so the
            // browser's natural new-tab + middle-click + Cmd/Ctrl-click
            // behaviour all work out of the box. The legacy
            // ``onAgentDrillNav`` prop is intentionally not invoked
            // here anymore (kept on the type to avoid churning every
            // caller and test).
            const href = buildUrl("agent-profile", slug, a.agent_id, {
              slice: null,
              team: teamId,
              timeRange: timeRange ?? null,
              rangeMode: rangeMode ?? null,
              focusedMeasureId: focused.measure_id,
              focusedLabelName: focused.label_name,
            });
            return (
              <a
                key={a.agent_id}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="focused-agent-row"
                className="w-full flex items-center justify-between gap-3 rounded px-3 py-2 text-left hover:bg-primary/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors"
              >
                <span className="text-sm text-foreground truncate flex-1">
                  {label}
                </span>
                {/* Task #316 — the bare percentage in the row has no
                    visible label, so wrap it in a tooltip that names
                    what the figure represents (detection rate for the
                    focused measure × label for this agent in the
                    selected period). */}
                <Tooltip
                  content={`Detection rate for ${focused.label_name} for ${label} in the selected period.`}
                >
                  <span
                    tabIndex={0}
                    className="text-xs text-muted-foreground tabular-nums focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}
                  >
                    {pct}%
                  </span>
                </Tooltip>
                {deltaText ? (
                  <Tooltip
                    content={`${deltaText} vs prior period — change in detection rate compared to the previous comparable window.`}
                  >
                    <span
                      tabIndex={0}
                      className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium tabular-nums focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${deltaTone}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                        }
                      }}
                    >
                      {deltaText}
                    </span>
                  </Tooltip>
                ) : (
                  <span className="text-[11px] text-muted-foreground/60 tabular-nums">
                    —
                  </span>
                )}
                <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                  {a.call_count.toLocaleString()} {callLabel}
                </span>
                <ArrowRight
                  className="h-4 w-4 text-muted-foreground shrink-0"
                  aria-hidden="true"
                />
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}
